/**
 * Platform capability detection.
 *
 * This module exists because of one specific, badly-documented Safari rule:
 *
 *   On iOS and iPadOS, Web Push works ONLY when the site has been added to the
 *   Home Screen and is running in standalone mode. In a normal Safari tab
 *   `Notification.requestPermission` is either missing entirely or resolves to
 *   a permission that never delivers anything.
 *
 * A generic "enable notifications" button therefore appears to work and then
 * silently does nothing forever — which is exactly the failure that made the
 * previous setup unusable. So instead of asking the browser a vague question,
 * we work out precisely which of the preconditions is unmet and tell the user
 * what to do about that one thing.
 */

export type PushBlocker =
  | 'none'
  | 'ios-needs-install'
  | 'ios-wrong-browser'
  | 'ios-version-too-old'
  | 'no-service-worker'
  | 'no-push-manager'
  | 'permission-denied'
  | 'unsupported-browser';

export interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isSafari: boolean;
  /**
   * Which browser is in use on iOS. Only Safari can install a web app that
   * receives push there — the others are WebKit wrappers whose "Add to Home
   * Screen" produces a shortcut, not an installed app.
   */
  iosBrowser: 'safari' | 'chrome' | 'firefox' | 'edge' | 'other' | null;
  /**
   * Firefox on Android. Its installed-web-app mode is far less complete than
   * Chrome's: it does not colour the system bars from `theme_color`, and its
   * push delivery to installed apps is unreliable. Worth telling people before
   * they trust it with the notifications this whole app exists for.
   */
  isFirefoxAndroid: boolean;
  /** Running as an installed app rather than in a browser tab. */
  isStandalone: boolean;
  iosVersion: number | null;
  supportsServiceWorker: boolean;
  supportsPush: boolean;
  supportsNotifications: boolean;
  permission: NotificationPermission | 'unsupported';
  /** The single thing preventing push from working, if anything. */
  blocker: PushBlocker;
  canEnablePush: boolean;
}

/** iPadOS 13+ reports itself as a Mac, so also check for touch support. */
function detectIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function detectIOSVersion(): number | null {
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (!match) return null;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  return Number.isFinite(major) ? major + minor / 100 : null;
}

function detectStandalone(): boolean {
  // iOS exposes a non-standard flag; everyone else uses the media query.
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  const displayMode = window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayMode;
}

/** Web Push landed in Safari 16.4, and only for installed web apps. */
const MIN_IOS_VERSION = 16.04;

/**
 * Identify the iOS browser. Every browser on iOS renders with WebKit, but only
 * Safari can install a web app capable of receiving push — Chrome, Firefox and
 * Edge run in their own containers and their "Add to Home Screen" does not
 * produce one.
 */
function detectIOSBrowser(): PlatformInfo['iosBrowser'] {
  const ua = navigator.userAgent;
  if (/CriOS/.test(ua)) return 'chrome';
  if (/FxiOS/.test(ua)) return 'firefox';
  if (/EdgiOS/.test(ua)) return 'edge';
  if (/Safari/.test(ua)) return 'safari';
  return 'other';
}

export function getPlatformInfo(): PlatformInfo {
  const isIOS = detectIOS();
  const isAndroid = /Android/.test(navigator.userAgent);
  const isFirefoxAndroid = isAndroid && /Firefox|FxiOS|Fennec/.test(navigator.userAgent);
  const iosBrowser = isIOS ? detectIOSBrowser() : null;
  const isSafari = isIOS
    ? iosBrowser === 'safari'
    : /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
  const isStandalone = detectStandalone();
  const iosVersion = isIOS ? detectIOSVersion() : null;

  const supportsServiceWorker = 'serviceWorker' in navigator;
  const supportsPush = 'PushManager' in window;
  const supportsNotifications = 'Notification' in window;

  const permission: NotificationPermission | 'unsupported' = supportsNotifications
    ? Notification.permission
    : 'unsupported';

  const blocker = determineBlocker({
    isIOS,
    iosBrowser,
    iosVersion,
    isStandalone,
    supportsServiceWorker,
    supportsPush,
    supportsNotifications,
    permission,
  });

  return {
    isIOS,
    isAndroid,
    isSafari,
    iosBrowser,
    isFirefoxAndroid,
    isStandalone,
    iosVersion,
    supportsServiceWorker,
    supportsPush,
    supportsNotifications,
    permission,
    blocker,
    canEnablePush: blocker === 'none',
  };
}

function determineBlocker(input: {
  isIOS: boolean;
  iosBrowser: PlatformInfo['iosBrowser'];
  iosVersion: number | null;
  isStandalone: boolean;
  supportsServiceWorker: boolean;
  supportsPush: boolean;
  supportsNotifications: boolean;
  permission: NotificationPermission | 'unsupported';
}): PushBlocker {
  // Check the iOS rules first: on iOS the generic capability checks below give
  // misleading answers in a plain Safari tab, so ordering matters here.
  if (input.isIOS) {
    if (input.iosVersion !== null && input.iosVersion < MIN_IOS_VERSION) {
      return 'ios-version-too-old';
    }
    // Sending someone through the install steps in Chrome for iOS wastes
    // their time: the resulting icon cannot receive push.
    if (!input.isStandalone && input.iosBrowser !== 'safari') {
      return 'ios-wrong-browser';
    }
    if (!input.isStandalone) {
      return 'ios-needs-install';
    }
  }

  if (!input.supportsServiceWorker) return 'no-service-worker';
  if (!input.supportsPush || !input.supportsNotifications) return 'no-push-manager';
  if (input.permission === 'denied') return 'permission-denied';
  if (input.permission === 'unsupported') return 'unsupported-browser';

  return 'none';
}

export interface BlockerExplanation {
  title: string;
  detail: string;
  /** Step-by-step instructions, when the user can fix it themselves. */
  steps?: string[];
  /** Whether showing an "enable" button makes any sense in this state. */
  actionable: boolean;
}

/**
 * Plain-language explanation of why notifications are not available, aimed at
 * a family member rather than a developer.
 */
export function explainBlocker(
  info: PlatformInfo,
): BlockerExplanation {
  switch (info.blocker) {
    case 'none':
      return {
        title: 'Notifications can be turned on',
        detail: 'This device supports notifications.',
        actionable: true,
      };

    case 'ios-needs-install':
      return {
        title: 'Add to your Home Screen first',
        detail:
          'On iPhone and iPad, notifications only work once this app is on your Home Screen. ' +
          'It takes about ten seconds, and you only do it once.',
        steps: [
          'Tap the Share button at the bottom of Safari (the square with an arrow pointing up).',
          'Scroll down and tap "Add to Home Screen".',
          'Tap "Add" in the top right corner.',
          'Close Safari and open the Family app from your Home Screen.',
          'Come back to this screen and turn notifications on.',
        ],
        actionable: false,
      };

    case 'ios-wrong-browser':
      return {
        title: 'Open this in Safari',
        detail:
          'On iPhone and iPad, only Safari can install an app that receives notifications. ' +
          'Adding it from this browser makes a shortcut that will stay silent.',
        steps: [
          'Copy this page\'s address.',
          'Open Safari and paste it in.',
          'Sign in again there, and the app will pick up where you left off.',
        ],
        actionable: false,
      };

    case 'ios-version-too-old':
      return {
        title: 'Your iPhone needs a software update',
        detail:
          `Notifications need iOS 16.4 or newer${
            info.iosVersion ? `, and this device is on ${info.iosVersion.toFixed(2).replace('.', '.')}` : ''
          }. Everything else in the app will still work.`,
        steps: ['Open Settings.', 'Tap General, then Software Update.', 'Install the update.'],
        actionable: false,
      };

    case 'permission-denied':
      return {
        title: 'Notifications are blocked',
        detail:
          'You (or someone) previously declined notifications for this app. ' +
          'The browser will not ask again, so it has to be changed in settings.',
        steps: info.isIOS
          ? [
              'Open the Settings app.',
              'Scroll down and tap Family (the app icon).',
              'Tap Notifications and switch "Allow Notifications" on.',
            ]
          : [
              'Tap the padlock or settings icon next to the address bar.',
              'Find Notifications and set it to Allow.',
              'Reload this page.',
            ],
        actionable: false,
      };

    case 'no-service-worker':
    case 'no-push-manager':
    case 'unsupported-browser':
      return {
        title: 'This browser cannot do notifications',
        detail:
          'Try opening the app in Safari on iPhone, or Chrome on Android or a computer. ' +
          'Private browsing windows also block notifications.',
        actionable: false,
      };

    default:
      return {
        title: 'Notifications are unavailable',
        detail: 'Something is preventing notifications on this device.',
        actionable: false,
      };
  }
}

export interface InstallGuidance {
  /** Heading for the install step. */
  title: string;
  detail: string;
  /** Manual steps, when the browser offers no install prompt of its own. */
  steps: string[];
  /** True when installing is required for notifications, not merely advised. */
  required: boolean;
  /**
   * True when installing resets the app's storage and forces a fresh sign-in.
   * iOS gives a Home Screen web app its own container, so the session does not
   * come across — worth saying out loud, or it looks like a bug.
   */
  losesSession: boolean;
}

export interface BrowserAdvice {
  title: string;
  detail: string;
  steps?: string[];
}

/**
 * A warning about the current browser's own limitations, separate from
 * whether push is technically available. Returns null when there is nothing
 * worth saying — most people should never see one of these.
 */
export function getBrowserAdvice(info: PlatformInfo): BrowserAdvice | null {
  if (info.isFirefoxAndroid) {
    return {
      title: 'Firefox may not notify you reliably',
      detail:
        'Firefox’s installed-app support on Android is incomplete: it leaves the bar at the ' +
        'top of the screen white instead of matching the app, and notifications often do not ' +
        'arrive. Installing from Chrome instead fixes both.',
      steps: [
        'Open Chrome and go to the same address.',
        'Sign in, then use the three-dot menu and tap "Install app".',
        'Turn on notifications there and send yourself a test.',
        'You can then remove the Firefox copy from your Home Screen.',
      ],
    };
  }

  return null;
}

/** Platform-specific instructions for getting the app onto the device. */
export function getInstallGuidance(info: PlatformInfo): InstallGuidance {
  if (info.isIOS) {
    return {
      title: 'Add Family to your Home Screen',
      detail:
        'This is what turns the page into a real app — and on iPhone it is the only way ' +
        'notifications can reach you.',
      steps: [
        'Tap the Share button at the bottom of the screen (a square with an arrow pointing up).',
        'Scroll down the list and tap "Add to Home Screen".',
        'Tap "Add" in the top right.',
        'Close Safari, then open Family from your Home Screen.',
        'Sign in once more there — iPhone keeps the app separate from Safari, so this is normal.',
      ],
      required: true,
      losesSession: true,
    };
  }

  if (info.isAndroid) {
    return {
      title: 'Install the app',
      detail:
        'Installing gives Family its own icon and makes notifications more reliable.',
      steps: [
        'Tap the three-dot menu in the top right of Chrome.',
        'Tap "Add to Home screen" or "Install app".',
        'Confirm with "Install".',
      ],
      required: false,
      losesSession: false,
    };
  }

  return {
    title: 'Install the app',
    detail: 'Optional on a computer, but it gives Family its own window and icon.',
    steps: [
      'Click the install icon at the right-hand end of the address bar.',
      'Confirm with "Install".',
    ],
    required: false,
    losesSession: false,
  };
}

/** Short label for the diagnostics list, e.g. "iPhone · Safari". */
export function describeDevice(userAgent: string): string {
  const ua = userAgent || navigator.userAgent;

  let device = 'Device';
  if (/iPhone/.test(ua)) device = 'iPhone';
  else if (/iPad/.test(ua)) device = 'iPad';
  else if (/Android/.test(ua)) device = 'Android';
  else if (/Macintosh/.test(ua)) device = 'Mac';
  else if (/Windows/.test(ua)) device = 'Windows PC';
  else if (/Linux/.test(ua)) device = 'Linux';

  let browser = '';
  if (/CriOS|Chrome/.test(ua)) browser = 'Chrome';
  else if (/FxiOS|Firefox/.test(ua)) browser = 'Firefox';
  else if (/Edg/.test(ua)) browser = 'Edge';
  else if (/Safari/.test(ua)) browser = 'Safari';

  return browser ? `${device} · ${browser}` : device;
}
